import { Backdrop, Divider, Grid } from '@mui/material';
import { isEmpty } from 'lodash';
import { useContext, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { AuthDataProvider } from 'src/components/authProvider';
import { ErrorInfo } from 'src/components/errors/ErrorInfo';
import { Progress, ProgressBox } from 'src/components/progress';
import {
  LOCATION_PAGE_CONTENT_VERTICAL_PADDING,
  ZOOM_BAR_HEIGHT,
} from 'src/constants';
import { useGetLocation } from 'src/hooks';

import { useLocationData } from './hooks/useLocationData';
import { useRBData } from './hooks/useRBData';
import { useTimeMachineParams } from './hooks/useTimeMachineParams';
import { TimeMachineHeader } from './modules/header/TimeMachineHeader';
import { TimeMachineInfo } from './modules/Info/TimeMachineInfo';
import LazyGcfPlayer from './modules/LazyGcfPlayer';
import { TimeMachineStepper } from './modules/stepper/TimeMachineStepper';
import { isAllStepCompleted } from './ViewLocationHistoryPage.helper';

export const ViewLocationHistoryPage = () => {
  const { userRole } = useContext(AuthDataProvider);
  const { locationCode = '' } = useParams();
  const { data: location } = useGetLocation(locationCode, userRole, {
    staleTime: Infinity,
  });
  const {
    timeMachineParams,
    onTimeMachineParamsChange,
    resetTimeMachineParams,
  } = useTimeMachineParams();

  const {
    locationData,
    locationDataHistories,
    isLocationDataLoading,
    isLocationDataFetching,
    isLocationDataSuccess,
    isLocationDataHistoriesFetching,
    isLocationDataHistoriesSuccess,
    locationVersion,
  } = useLocationData(
    locationCode,
    timeMachineParams.date,
    timeMachineParams.time,
    location?.hq?.timezone,
  );

  const {
    rbData,
    rbDataHistories,
    isRBDataFetching,
    isRBDataSuccess,
    isRBDataHistoriesSuccess,
  } = useRBData(
    locationCode,
    timeMachineParams.date,
    timeMachineParams.time,
    location?.hq?.timezone,
  );

  useEffect(() => {
    let originalGcfComponentsUrl = '';
    let originalGcfCodUrl = '';

    if (locationData) {
      // store original urls
      originalGcfComponentsUrl = window.environment.urls.gcfComponentsUrl;
      originalGcfCodUrl = window.environment.urls.gcfCodUrl;

      // override urls
      window.environment.urls.gcfComponentsUrl =
        locationData?.projectConfig?.gcf?.apps?.components?.url;
      window.environment.urls.gcfCodUrl =
        locationData?.projectConfig?.gcf?.apps?.cod?.url;
    }

    return () => {
      // restore urls
      window.environment.urls.gcfComponentsUrl = originalGcfComponentsUrl
        ? originalGcfComponentsUrl
        : window.environment.urls.gcfComponentsUrl;
      window.environment.urls.gcfCodUrl = originalGcfCodUrl
        ? originalGcfCodUrl
        : window.environment.urls.gcfCodUrl;
    };
  }, [locationData]);

  const isDataReady =
    (isAllStepCompleted(timeMachineParams) &&
      isLocationDataSuccess &&
      isRBDataSuccess) ||
    (isRBDataHistoriesSuccess && isEmpty(rbDataHistories)) ||
    (isLocationDataHistoriesSuccess && isEmpty(locationDataHistories));
  const shouldDisplayError = isEmpty(locationData) || isEmpty(rbData);

  // Reserve the ScaleBar height (ZOOM_BAR_HEIGHT) for the player controls while
  // reclaiming the Content area's top padding so the player stays vertically
  // centered in the preview pane. Net offset = 38px.
  const previewHeight = `calc(100% - ${ZOOM_BAR_HEIGHT}px + ${LOCATION_PAGE_CONTENT_VERTICAL_PADDING}px)`;

  return (
    <Grid container width="100%" height="100%" direction="column" wrap="nowrap">
      <Grid container flex={1}>
        <TimeMachineHeader location={location} />
      </Grid>

      <Grid item>
        <Divider />
      </Grid>

      <Grid
        container
        flex={10}
        maxHeight={'100%'}
        width={'100%'}
        flexWrap={'nowrap'}
        overflow={'hidden'}>
        <Grid
          item
          flex={2}
          position="relative"
          width={'100%'}
          maxWidth={'30%'}
          minWidth="262px"
          padding={1}>
          <TimeMachineStepper
            timeMachineParams={timeMachineParams}
            onTimeMachineParamsChange={onTimeMachineParamsChange}
            resetTimeMachineParams={resetTimeMachineParams}
            channelOptions={Object.keys(locationData?.location?.channels ?? [])}
            hasLocationVersion={!!locationVersion?.length}
            isChannelOptionsLoading={
              isLocationDataLoading &&
              isLocationDataFetching &&
              isLocationDataHistoriesFetching
            }
          />

          <TimeMachineInfo />

          <Backdrop
            sx={{
              bgcolor: 'brand.whitesmoke',
              opacity: '0.7!important',
              position: 'absolute',
            }}
            open={
              isLocationDataFetching ||
              isLocationDataHistoriesFetching ||
              isRBDataFetching
            }>
            <Progress />
          </Backdrop>
        </Grid>

        <Divider flexItem orientation="vertical" />

        <Grid
          container
          justifyContent="center"
          alignItems="center"
          flex={10}
          height={previewHeight}
          width={'70%'}>
          {isDataReady ? (
            shouldDisplayError ? (
              <ErrorInfo
                title="Oh no! Looks like there were no assemblies playing or the data source wasn’t available at this time."
                info="Please select another date or time and try again."
              />
            ) : (
              locationData?.projectConfig?.gcf?.apps?.player?.url && (
                <LazyGcfPlayer
                  key={`${timeMachineParams.date}-${timeMachineParams.time}-${timeMachineParams.channel}`}
                  url={locationData.projectConfig.gcf.apps.player.url}
                />
              )
            )
          ) : (
            <ProgressBox loading={false} />
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};
